/**
 * Thread Admin Panel
 * 
 * Manages thread operations:
 * - Thread creation and lookup
 * - Participant management (add/remove)
 * - Message viewing and batch operations
 * - Statistics (unread, participant counts)
 */

import { useState, useEffect } from "react";
import { messageStructureSchemas, type messageStructureTypes } from "@pineseed/spec";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { EntitySelector } from "../components/EntitySelector";
import { useAdmin } from "../context/AdminContext";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";
import { v7 as uuidv7 } from "uuid";

type Thread = messageStructureTypes["Thread"];

type View = 'threads' | 'overview' | 'participants' | 'stats';

export function ThreadPanel() {
  const { adminApp, thread } = useAdmin();
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  const [view, setView] = useState<View>('threads');
  
  // Pagination for thread list
  const [page, setPage] = useState(0);
  const [limit] = useState(50);

  // Load threads when on list view or page changes
  useEffect(() => {
    if (view === 'threads') {
      thread.loadThreads(limit, page * limit);
    }
  }, [view, page, limit]); // Don't include thread - it's from context

  // Thread management
  const [threadIdInput, setThreadIdInput] = useState('');
  const [showCreateThreadForm, setShowCreateThreadForm] = useState(false);
  
  // Participant management
  const [showAddParticipantForm, setShowAddParticipantForm] = useState(false);
  const [participantIdToAdd, setParticipantIdToAdd] = useState('');
  
  // Message batch add
  const [showBatchMessagesForm, setShowBatchMessagesForm] = useState(false);
  const [batchMessagesJson, setBatchMessagesJson] = useState('');
  const [batchMessagesError, setBatchMessagesError] = useState('');

  // Stats
  const [statsParticipantId, setStatsParticipantId] = useState('');
  
  // WebSocket subscription (isolated from global subscription controls)
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Subscribe to thread events when enabled (uses hook's subscribeToThread for message handling)
  useEffect(() => {
    if (!thread.currentThread || !isSubscribed) return;

    console.log('🔌 [ThreadPanel] Subscribing to thread:', thread.currentThread.threadId);
    const unsubscribe = thread.subscribeToThread(
      thread.currentThread.workspaceId,
      thread.currentThread.conversationId,
      thread.currentThread.threadId
    );

    return () => {
      console.log('🔴 [ThreadPanel] Unsubscribing from thread');
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.currentThread, isSubscribed]);

  // Reset subscription toggle when thread changes
  useEffect(() => {
    setIsSubscribed(false);
  }, [thread.currentThread?.threadId]);

  const handleLoadThread = async () => {
    if (!threadIdInput.trim()) return;
    await thread.getThread(threadIdInput.trim());
    
    // Auto-load participants and messages
    if (threadIdInput.trim()) {
      await thread.getThreadParticipants(threadIdInput.trim());
      await thread.getThreadMessages(threadIdInput.trim());
    }
  };

  const handleCreateThread = async (data: Partial<Thread>) => {
    const threadData = {
      threadId: data.threadId || uuidv7(),
      conversationId: data.conversationId!,
      workspaceId: data.workspaceId || adminApp.workspaceId,
    };
    
    await thread.createThread(threadData);
    setShowCreateThreadForm(false);
    
    // Auto-load participants and messages for new thread
    await thread.getThreadParticipants(threadData.threadId);
    await thread.getThreadMessages(threadData.threadId);
  };

  const handleAddParticipant = async () => {
    if (!thread.currentThread || !participantIdToAdd.trim()) return;
    
    await thread.addParticipant(thread.currentThread.threadId, participantIdToAdd.trim());
    setParticipantIdToAdd('');
    setShowAddParticipantForm(false);
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!thread.currentThread) return;
    if (!confirm(`Remove participant ${participantId}?`)) return;
    
    await thread.removeParticipant(thread.currentThread.threadId, participantId);
  };


  const handleLoadStats = async () => {
    if (!thread.currentThread || !statsParticipantId.trim()) return;
    
    await thread.loadStats(thread.currentThread.threadId, statsParticipantId.trim());
  };

  const handleLoadExampleMessages = () => {
    if (!thread.currentThread) return;
    
    // Generate timestamps spread over a day (24 hours)
    const now = new Date();
    const baseTime = now.getTime() - (24 * 60 * 60 * 1000); // 24 hours ago
    
    const exampleMessages = [
      {
        id: uuidv7(),
        content: "How do I implement a simple REST API endpoint in TypeScript?",
        role: "registered",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "ce2c5abef195ab8baff6b5fb6f918d6842fb677c354327a1486d0c983a6d67ae",
        timestamp: new Date(baseTime).toISOString(),
        reasoning: {
          steps: ["User needs guidance on REST API implementation", "Wants TypeScript-specific approach"],
          conclusion: "Question about basic REST API setup"
        }
      },
      {
        id: uuidv7(),
        content: "Here's a basic example using Express:\n\n```typescript\nimport express from 'express';\n\nconst app = express();\n\napp.get('/api/users', (req, res) => {\n  res.json({ users: [] });\n});\n\napp.listen(3000);\n```",
        role: "assistant",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "7f8e9d1c2b3a4f5e6d7c8b9a0f1e2d3c4b5a6f7e8d9c0b1a2f3e4d5c6b7a8f9",
        timestamp: new Date(baseTime + (1 * 3 * 60 * 60 * 1000)).toISOString(),
        reasoning: {
          steps: ["Provide simple Express.js example", "Show TypeScript types", "Include basic GET endpoint"],
          conclusion: "Minimal working example provided"
        }
      },
      {
        id: uuidv7(),
        content: "Thanks! How do I add request validation?",
        role: "registered",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "ce2c5abef195ab8baff6b5fb6f918d6842fb677c354327a1486d0c983a6d67ae",
        timestamp: new Date(baseTime + (2 * 3 * 60 * 60 * 1000)).toISOString(),
        reasoning: {
          steps: ["Follow-up on previous answer", "Needs validation layer"],
          conclusion: "Asking for request validation approach"
        }
      },
      {
        id: uuidv7(),
        content: "You can use Zod for validation:\n\n```typescript\nimport { z } from 'zod';\n\nconst userSchema = z.object({\n  name: z.string().min(1),\n  email: z.string().email()\n});\n\napp.post('/api/users', (req, res) => {\n  const result = userSchema.safeParse(req.body);\n  if (!result.success) {\n    return res.status(400).json({ error: result.error });\n  }\n  res.json({ user: result.data });\n});\n```",
        role: "assistant",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "7f8e9d1c2b3a4f5e6d7c8b9a0f1e2d3c4b5a6f7e8d9c0b1a2f3e4d5c6b7a8f9",
        timestamp: new Date(baseTime + (3 * 3 * 60 * 60 * 1000)).toISOString(),
        reasoning: {
          steps: ["Recommend Zod library", "Show schema definition", "Demonstrate validation in endpoint"],
          conclusion: "Complete validation example with error handling"
        }
      },
      {
        id: uuidv7(),
        content: "Can another user join this conversation?",
        role: "system",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "system-notification-handler-1234567890abcdef",
        timestamp: new Date(baseTime + (4 * 3 * 60 * 60 * 1000)).toISOString(),
        reasoning: {
          steps: ["System notification about potential participant", "Check thread capacity"],
          conclusion: "Notification about thread access request"
        }
      },
      {
        id: uuidv7(),
        content: "This is really helpful! One more thing - how do I handle errors globally?",
        role: "registered",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "ce2c5abef195ab8baff6b5fb6f918d6842fb677c354327a1486d0c983a6d67ae",
        timestamp: new Date(baseTime + (5 * 3 * 60 * 60 * 1000)).toISOString(),
        reasoning: {
          steps: ["User wants error handling pattern", "Looking for middleware approach"],
          conclusion: "Question about global error handling"
        }
      },
      {
        id: uuidv7(),
        content: "Use Express error middleware:\n\n```typescript\napp.use((err: Error, req, res, next) => {\n  console.error(err.stack);\n  res.status(500).json({ \n    error: 'Internal server error',\n    message: err.message \n  });\n});\n```\n\nPlace this after all routes.",
        role: "assistant",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "7f8e9d1c2b3a4f5e6d7c8b9a0f1e2d3c4b5a6f7e8d9c0b1a2f3e4d5c6b7a8f9",
        timestamp: new Date(baseTime + (6 * 3 * 60 * 60 * 1000)).toISOString(),
        reasoning: {
          steps: ["Show error middleware pattern", "Include logging", "Explain placement importance"],
          conclusion: "Global error handler with proper structure"
        }
      },
      {
        id: uuidv7(),
        content: "Perfect! That's exactly what I needed.",
        role: "registered",
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        participantId: "ce2c5abef195ab8baff6b5fb6f918d6842fb677c354327a1486d0c983a6d67ae",
        timestamp: new Date(baseTime + (7 * 3 * 60 * 60 * 1000)).toISOString(),
        reasoning: {
          steps: ["User satisfied with answers", "Conversation concluding"],
          conclusion: "Positive acknowledgment"
        }
      }
    ];
    
    setBatchMessagesJson(JSON.stringify(exampleMessages, null, 2));
  };

  const handleBatchAddMessages = async () => {
    if (!thread.currentThread) return;
    
    setBatchMessagesError('');
    
    try {
      // Parse and validate JSON
      const parsed = JSON.parse(batchMessagesJson);
      
      if (!Array.isArray(parsed)) {
        setBatchMessagesError('Input must be a JSON array');
        return;
      }
      
      // Validate each message against ChatMessage schema
      const validatedMessages = parsed.map((msg, idx) => {
        const result = messageStructureSchemas.ChatMessage.safeParse(msg);
        if (!result.success) {
          throw new Error(`Message ${idx}: ${result.error.issues[0].message}`);
        }
        return result.data;
      });
      
      // Add messages
      await thread.addMessagesToThread({
        threadId: thread.currentThread.threadId,
        conversationId: thread.currentThread.conversationId,
        workspaceId: thread.currentThread.workspaceId,
        messages: validatedMessages as any,
      });
      
      setShowBatchMessagesForm(false);
      setBatchMessagesJson('');
    } catch (err) {
      setBatchMessagesError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  };

  // Participant columns
  const participantColumns: Column<{ id: string; participantId: string }>[] = [
    {
      key: 'participantId',
      label: 'Participant ID',
      render: (row) => (
        <span className="font-mono text-sm">{row.participantId}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          onClick={() => handleRemoveParticipant(row.participantId)}
          className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
        >
          Remove
        </button>
      ),
    },
  ];


  const handleSelectThread = async (t: Thread) => {
    setThreadIdInput(t.threadId);
    await thread.getThread(t.threadId);
    await thread.getThreadParticipants(t.threadId);
    await thread.getThreadMessages(t.threadId);
    setView('overview');
  };

  const threadColumns: Column<Thread>[] = [
    {
      key: 'threadId',
      label: 'Thread ID',
      sortable: true,
      render: (row) => (
        <CopyableText id={row.threadId} onCopy={copyToClipboard} isPulsing={isCopied(row.threadId)} variant="muted">
          {row.threadId.slice(0, 8)}...
        </CopyableText>
      ),
    },
    {
      key: 'conversationId',
      label: 'Conversation ID',
      sortable: true,
      render: (row) => (
        <CopyableText id={row.conversationId} onCopy={copyToClipboard} isPulsing={isCopied(row.conversationId)} variant="muted">
          {row.conversationId.slice(0, 8)}...
        </CopyableText>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      render: (row) => <span className="text-sm">{new Date(row.updatedAt).toLocaleString()}</span>,
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          onClick={() => handleSelectThread(row)}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Thread Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage threads, participants, and messages
          </p>
        </div>
        
        <div className="flex gap-2">
          {thread.currentThread && (
            <button
              onClick={() => { thread.clearThread(); setView('threads'); }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium text-sm"
            >
              ← Back to List
            </button>
          )}
          <button
            onClick={() => setShowCreateThreadForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
          >
            Create Thread
          </button>
        </div>
      </div>

      {/* Threads List */}
      {!thread.currentThread && view === 'threads' && (
        <div className="space-y-4">
          <EntityTable<Thread>
            data={thread.threads}
            columns={threadColumns}
            loading={thread.loading}
            emptyMessage="No threads found in this workspace"
          />
          {/* Pagination */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0 || thread.loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">Page {page + 1}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={thread.threads.length < limit || thread.loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              Next
            </button>
          </div>
          {thread.error && (
            <p className="text-sm text-red-600 dark:text-red-400">{thread.error}</p>
          )}
        </div>
      )}

      {/* Thread Lookup (fallback when not in list view and no thread selected) */}
      {!thread.currentThread && view !== 'threads' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Load Thread
          </h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={threadIdInput}
              onChange={(e) => setThreadIdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLoadThread()}
              placeholder="Enter thread ID..."
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={handleLoadThread}
              disabled={!threadIdInput.trim() || thread.loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {thread.loading ? 'Loading...' : 'Load'}
            </button>
          </div>
          {thread.error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{thread.error}</p>
          )}
        </div>
      )}

      {/* Thread Details */}
      {thread.currentThread && (
        <>
          {/* Current Thread Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Thread Details</h3>
              
              {/* WebSocket Subscription Toggle */}
              <button
                onClick={() => setIsSubscribed(!isSubscribed)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium flex items-center gap-2 ${
                  isSubscribed
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${isSubscribed ? 'bg-green-500' : 'bg-gray-400'}`} />
                {isSubscribed ? 'Live Updates ON' : 'Live Updates OFF'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Thread ID</label>
                <p className="font-mono text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {thread.currentThread.threadId}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Conversation ID</label>
                <p className="font-mono text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {thread.currentThread.conversationId}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Workspace ID</label>
                <p className="font-mono text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {thread.currentThread.workspaceId}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Created</label>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {new Date(thread.currentThread.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* View Tabs */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
            <nav className="flex space-x-1 px-4" aria-label="Tabs">
              {(['overview', 'participants', 'stats'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setView(tab)}
                  className={`
                    px-4 py-3 text-sm font-medium capitalize
                    border-b-2 transition-colors
                    ${view === tab
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }
                  `}
                >
                  {tab}
                  {tab === 'participants' && thread.participants.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 dark:bg-gray-700">
                      {thread.participants.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* View Content */}
          <div className="bg-white dark:bg-gray-800 rounded-b-lg border border-gray-200 dark:border-gray-700 border-t-0 p-6">
            {view === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Participants</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                      {thread.participants.length}
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Messages</div>
                      <button
                        onClick={() => setShowBatchMessagesForm(true)}
                        className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        title="Batch add messages"
                      >
                        + Batch
                      </button>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                      {thread.messages.length}
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Updated</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                      {new Date(thread.currentThread.updatedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {view === 'participants' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Participants ({thread.participants.length})
                  </h3>
                  <button
                    onClick={() => setShowAddParticipantForm(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                  >
                    Add Participant
                  </button>
                </div>

                {thread.participants.length === 0 ? (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No participants in this thread
                  </p>
                ) : (
                  <EntityTable
                    data={thread.participants.map(p => ({ id: p, participantId: p }))}
                    columns={participantColumns}
                    loading={thread.loading}
                  />
                )}
              </div>
            )}

            {view === 'stats' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Statistics</h3>
                
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Participant ID (for unread count)
                    </label>
                    <EntitySelector
                      entityType="participant"
                      value={statsParticipantId}
                      onChange={setStatsParticipantId}
                      placeholder="Select participant..."
                    />
                  </div>
                  <button
                    onClick={handleLoadStats}
                    disabled={!statsParticipantId.trim() || thread.loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Load Stats
                  </button>
                </div>

                {thread.stats && (
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Participant Count
                      </div>
                      <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                        {thread.stats.participantCount}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Unread Messages
                      </div>
                      <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                        {thread.stats.unreadCount}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Thread Modal */}
      <Modal
        isOpen={showCreateThreadForm}
        onClose={() => setShowCreateThreadForm(false)}
        title="Create Thread"
      >
        <div className="p-4">
          <EntityForm
            schema={messageStructureSchemas.Thread.pick({
              threadId: true,
              conversationId: true,
              workspaceId: true,
            })}
            onSubmit={handleCreateThread}
            onCancel={() => setShowCreateThreadForm(false)}
            submitLabel="Create"
            excludeSelectors={['threadId']}
          />
        </div>
      </Modal>

      {/* Add Participant Modal */}
      <Modal
        isOpen={showAddParticipantForm}
        onClose={() => setShowAddParticipantForm(false)}
        title="Add Participant"
      >
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Participant ID
            </label>
            <EntitySelector
              entityType="participant"
              value={participantIdToAdd}
              onChange={setParticipantIdToAdd}
              placeholder="Select participant..."
            />
          </div>
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowAddParticipantForm(false);
                setParticipantIdToAdd('');
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleAddParticipant}
              disabled={!participantIdToAdd.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>
      </Modal>

      {/* Batch Add Messages Modal */}
      <Modal
        isOpen={showBatchMessagesForm}
        onClose={() => {
          setShowBatchMessagesForm(false);
          setBatchMessagesJson('');
          setBatchMessagesError('');
        }}
        title="Batch Add Messages"
        maxWidth="2xl"
      >
        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Messages JSON Array
              </label>
              <button
                onClick={handleLoadExampleMessages}
                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Load Example
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Paste a JSON array of message objects (with <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">id</code> and <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">timestamp</code>)
            </p>
            <textarea
              value={batchMessagesJson}
              onChange={(e) => setBatchMessagesJson(e.target.value)}
              placeholder={`[
  {
    "id": "uuid-v7",
    "content": "Message text",
    "role": "registered",
    "threadId": "${thread.currentThread?.threadId || 'thread-id'}",
    "conversationId": "${thread.currentThread?.conversationId || 'conversation-id'}",
    "workspaceId": "${thread.currentThread?.workspaceId || 'workspace-id'}",
    "participantId": "participant-id",
    "timestamp": "2024-01-20T12:00:00.000Z"
  }
]`}
              className="w-full h-64 px-3 py-2 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>

          {batchMessagesError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200">
                ❌ {batchMessagesError}
              </p>
            </div>
          )}
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowBatchMessagesForm(false);
                setBatchMessagesJson('');
                setBatchMessagesError('');
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleBatchAddMessages}
              disabled={!batchMessagesJson.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Messages
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

